import {
    ModalButtonStyles,
    OpenModal,
    useEnum,
    useMask,
    useTable,
} from '@composables/common'
import BankAccountCreateEditForm from '@partials/accounts/BankAccountCreateEditForm.vue'
import AuditsTable from '@partials/audits/AuditsTable.vue'
import NameAndId from '@src/components/NameAndId.vue'
import { BankAccountStatus } from '@src/enums'
import { BankAccount } from '@src/types/Profiles'
import {
    createBankAccountForLegalProfile,
    createBankAccountForNaturalProfile,
    deleteBankAccount,
    updateBankAccount,
} from '@src/utils/api/bank-accounts'
import { useAuditsStore } from '@src/utils/stores/audits'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import { AxiosError } from 'axios'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { Ref, computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function useBankAccountsTable(
    bankAccounts: Ref<BankAccount[]>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>,
    profileId: Ref<string | null | undefined>,
    profileType: 'natural' | 'legal',
    platformName: Ref<string | null | undefined>,
    reload: () => Promise<void>
) {
    const { getEnumLabel } = useEnum()
    const { everyFourSpace } = useMask()
    const i18n = useI18n()
    const toast = useToastStore()
    const userStore = useUserStore()
    const auditsStore = useAuditsStore()
    const { auditedModels } = storeToRefs(auditsStore)

    const total = computed(() => {
        return bankAccounts.value.length
    })

    const { TableWrapper: BankAccountsTable } = useTable({
        tableProps: {
            data: bankAccounts,
            total: total,
            label: 'account_bank_account_headline',
            id: 'help_bank_accounts_table',
            help: 'help_bank_accounts_table',
            columns: [
                {
                    label: 'account_holder',
                    key: 'holder',
                    sorting: false,
                    copy: true,
                },
                {
                    label: 'iban',
                    key: 'iban',
                    sorting: false,
                    cellRenderer: (props: { rowData: BankAccount }) => {
                        return (
                            props.rowData.iban &&
                            everyFourSpace(props.rowData.iban)
                        )
                    },
                    copy: true,
                },
                {
                    label: 'bic',
                    key: 'bic',
                    sorting: false,
                    copy: true,
                },
                {
                    label: 'status',
                    key: 'status',
                    sorting: false,
                    cellRenderer: (props: { rowData: BankAccount }) => {
                        return getEnumLabel(
                            BankAccountStatus,
                            props.rowData.status
                        )
                    },
                },
                {
                    label: 'primary_for_platforms',
                    key: 'primary_for_platforms',
                    sorting: false,
                    cellRenderer: (props: { rowData: BankAccount }) => {
                        return (props.rowData.primary_for_platforms || []).join(
                            ', '
                        )
                    },
                },
                {
                    label: 'created_at',
                    key: 'created_at',
                    sorting: false,
                    cellRenderer: (props: { rowData: BankAccount }) => {
                        return moment(props.rowData.created_at).format(
                            'DD.MM.yyyy'
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleSetPrimary,
                    icon: 'fa-solid fa-star',
                    title: 'help_set_as_primary',
                    condition: (bankAccount: BankAccount) =>
                        userStore.can('update_account') &&
                        bankAccount.status === BankAccountStatus.VERIFIED &&
                        !isPrimaryForCurrentPlatform(bankAccount),
                },
                {
                    action: handleEdit,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: () => userStore.can('update_account'),
                },
                {
                    action: handleViewAudits,
                    icon: 'fa-solid fa-clock-rotate-left',
                    title: 'help_view_bank_account_audits',
                },
                {
                    action: handleDelete,
                    icon: 'fa-solid fa-trash-can',
                    title: 'delete',
                    condition: () => userStore.can('update_account'),
                },
            ],
        },
    })

    function isPrimaryForCurrentPlatform(bankAccount: BankAccount): boolean {
        if (!platformName?.value) {
            return false
        }
        return (bankAccount.primary_for_platforms || []).includes(
            platformName.value
        )
    }

    function handleCreate() {
        const newBankAccount = ref(new BankAccount())
        const isFormValid = ref(false)
        isModalOpen.value = true

        openModal(
            {
                id: 'create_bank_account',
                title: 'modal_title_create_bank_account',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => saveNewBankAccount(newBankAccount.value),
            },
            () =>
                h(BankAccountCreateEditForm, {
                    'modelValue': newBankAccount,
                    'onUpdate:valid': (valid: boolean) => {
                        isFormValid.value = valid
                    },
                }),
            {
                submitButtonText: 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: computed(() => !isFormValid.value),
            }
        )
    }

    async function saveNewBankAccount(bankAccount: BankAccount) {
        try {
            if (!profileId?.value) {
                return
            }
            const payload = {
                holder: bankAccount.holder || '',
                iban: bankAccount.iban || '',
                bic: bankAccount.bic,
            }
            const response =
                profileType === 'natural'
                    ? await createBankAccountForNaturalProfile(
                          profileId.value,
                          payload
                      )
                    : await createBankAccountForLegalProfile(
                          profileId.value,
                          payload
                      )
            bankAccounts.value.push(response.data)
            toast.success(i18n.t('toast_success_bank_account_created'))
        } catch (error) {
            toast.error(i18n.t('toast_error_bank_account_create'))
            throw error
        }
        isModalOpen.value = false
    }

    function handleEdit(bankAccount: BankAccount) {
        const editedBankAccount = ref({ ...bankAccount })
        const isFormValid = ref(true)
        isModalOpen.value = true

        openModal(
            {
                id: 'update_bank_account',
                title: 'modal_title_update_bank_account',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => saveEditedBankAccount(editedBankAccount.value),
            },
            () =>
                h(BankAccountCreateEditForm, {
                    'modelValue': editedBankAccount,
                    'onUpdate:valid': (valid: boolean) => {
                        isFormValid.value = valid
                    },
                }),
            {
                submitButtonText: 'update',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: computed(() => !isFormValid.value),
            }
        )
    }

    async function saveEditedBankAccount(bankAccount: BankAccount) {
        try {
            if (!bankAccount.id) {
                return
            }
            const response = await updateBankAccount(bankAccount.id, {
                holder: bankAccount.holder || '',
                iban: bankAccount.iban || '',
                bic: bankAccount.bic,
            })
            const index = bankAccounts.value.findIndex(
                (account) => account.id === bankAccount.id
            )
            if (index !== -1) {
                bankAccounts.value[index] = response.data
            }
            toast.success(i18n.t('toast_success_bank_account_updated'))
        } catch (error) {
            toast.error(i18n.t('toast_error_bank_account_update'))
            throw error
        }
        isModalOpen.value = false
    }

    async function handleSetPrimary(bankAccount: BankAccount) {
        if (!bankAccount.id || !platformName?.value) {
            return
        }
        try {
            await updateBankAccount(bankAccount.id, {
                is_primary: true,
                platform_name: platformName.value,
            })
            await reload()
            toast.success(i18n.t('toast_success_bank_account_updated'))
        } catch (error) {
            toast.error(i18n.t('toast_error_bank_account_update'))
        }
    }

    function handleDelete(bankAccount: BankAccount) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_bank_account',
                title: 'modal_title_delete_bank_account',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteBankAccountConfirmed(bankAccount),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'holder',
                    modelValue: [bankAccount],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    async function deleteBankAccountConfirmed(bankAccount: BankAccount) {
        try {
            if (!bankAccount.id) {
                return
            }
            await deleteBankAccount(bankAccount.id)
            bankAccounts.value = bankAccounts.value.filter(
                (account) => account.id !== bankAccount.id
            )
            toast.success(i18n.t('toast_success_bank_account_deleted'))
        } catch (error) {
            const message = (error as AxiosError<{ message?: string }>)
                ?.response?.data?.message
            toast.error(message || i18n.t('toast_error_bank_account_delete'))
            throw error
        }
        isModalOpen.value = false
    }

    function handleViewAudits(bankAccount: BankAccount) {
        isModalOpen.value = true
        openModal(
            {
                id: 'view_bank_account_audits',
                title: 'help_view_bank_account_audits',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
            },
            () =>
                h(AuditsTable, {
                    initialFilters: {
                        auditable_id: bankAccount.id,
                        auditable_type: auditedModels.value.bank_account.value,
                    },
                })
        )
    }

    return {
        BankAccountsTable,
        handleCreate,
    }
}
